import dayjs from "dayjs";

import { DEFAULT_RATE_OF_RETURN } from "@constants";
import { Goal, SipFrequency } from "@ctypes";
import { calculateProjectedAmount } from "./chart";
import { convertCentsToDollar } from "./formatter";
import { convertToAnnualAmount } from "./goals";

export enum Standing {
  Met = "MET",
  OnTrack = "ON_TRACK",
  Short = "SHORT",
}

export type GoalStanding = {
  goal: Goal;
  status: Standing;
  current: number;
  target: number;
  projectedAtTarget: number;
  targetYear: number;
  startYear: number;
  /** Extra monthly contribution that closes the gap; null when met/on track
      or when the target date has passed. */
  remedyMonthly: number | null;
};

/** Active vs paused recurring deposits, in dollars per year. A paused sip
    must never count toward a projection. */
export const annualDepositBreakdown = (goal?: Goal | null) => {
  let activeAnnual = 0;
  let pausedAnnual = 0;

  goal?.sips?.forEach((sip) => {
    if (!sip) return;

    const annual = convertCentsToDollar(
      convertToAnnualAmount({
        recurringDepositInCents: sip.recurringDepositInCents,
        frequency: sip.recurringDepositSchedule,
      }),
    );

    if (sip.paused) {
      pausedAnnual += annual;
    } else {
      activeAnnual += annual;
    }
  });

  goal?.partners?.forEach((partner) => {
    if (!partner) return;

    activeAnnual += convertCentsToDollar(
      convertToAnnualAmount({
        recurringDepositInCents: partner.recurringDepositInCents,
        frequency: partner.recurringDepositSchedule,
      }),
    );
  });

  return { activeAnnual, pausedAnnual };
};

export const goalRate = (goal?: Goal | null) =>
  (goal?.projectedRateOfReturn ?? DEFAULT_RATE_OF_RETURN) / 100;

export const goalYearsRemaining = (goal?: Goal | null) => {
  const targetYear = goal?.endDate ? dayjs(goal.endDate).year() : dayjs().year();
  return Math.max(targetYear - dayjs().year(), 0);
};

/** Future value of the goal under a given annual contribution. */
export const projectGoal = (goal: Goal, annualDeposit: number) => {
  const years = goalYearsRemaining(goal);
  const balance = convertCentsToDollar(goal.currentAmountInCents);

  if (years === 0) return balance;

  return calculateProjectedAmount({
    amount: annualDeposit,
    recurringDepositType: SipFrequency.Annually,
    currentAge: 0,
    projectedAge: years,
    contributeAfter18: goal.contributionsAfter18 ?? true,
    initialBalance: balance,
    multiplier: goalRate(goal) + 1,
  });
};

/** The annual contribution that exactly reaches the target: a real solve,
    not copy. */
export const solveAnnualContribution = (goal: Goal) => {
  const years = goalYearsRemaining(goal);
  if (years === 0) return null;

  const rate = goalRate(goal);
  const target = convertCentsToDollar(goal.targetAmountInCents);
  const balance = convertCentsToDollar(goal.currentAmountInCents);

  const balanceGrowth = balance * Math.pow(1 + rate, years);
  const annuityFactor = rate > 0 ? (Math.pow(1 + rate, years) - 1) / rate : years;

  const needed = (target - balanceGrowth) / annuityFactor;

  return Math.max(needed, 0);
};

/** Status is derived, never stored: compare the goal's own projection at its
    target date against its target amount. */
export const deriveGoalStanding = (goal: Goal): GoalStanding => {
  const nowYear = dayjs().year();

  const current = convertCentsToDollar(goal.currentAmountInCents);
  const target = convertCentsToDollar(goal.targetAmountInCents);
  const targetYear = goal.endDate ? dayjs(goal.endDate).year() : nowYear;
  const startYear = goal.startDate ? dayjs(goal.startDate).year() : nowYear;

  const { activeAnnual } = annualDepositBreakdown(goal);
  const projectedAtTarget = projectGoal(goal, activeAnnual);

  const status = (() => {
    if (target > 0 && current >= target) return Standing.Met;
    // Within half a percent of the target counts as on track – a "$0 gap"
    // produced by rounding should not read as falling short.
    if (projectedAtTarget >= target * 0.995) return Standing.OnTrack;
    return Standing.Short;
  })();

  const remedyMonthly = (() => {
    if (status !== Standing.Short) return null;

    const neededAnnual = solveAnnualContribution(goal);
    if (neededAnnual == null) return null;

    const extraMonthly = (neededAnnual - activeAnnual) / 12;

    return Math.max(Math.ceil(extraMonthly / 5) * 5, 5);
  })();

  return {
    goal,
    status,
    current,
    target,
    projectedAtTarget,
    targetYear,
    startYear,
    remedyMonthly,
  };
};
